import { createFallbackProjects } from '../data/fallbackProjects';
import type { ProjectManifest, ProjectRegistryResponse } from '../types/project';
import { isVisibleProject } from '../types/project';

export interface LoadedProjects {
  projects: ProjectManifest[];
  source: 'remote' | 'fallback';
  warning?: string;
}

function normalizeProject(project: ProjectManifest, origin: string): ProjectManifest {
  const runtimeUrl = project.runtimeUrl || `${origin}/projects/${encodeURIComponent(project.slug)}/`;
  const manifestUrl = project.manifestUrl || `${origin}/projects/${encodeURIComponent(project.slug)}/_project.json`;

  return {
    ...project,
    runtimeUrl,
    manifestUrl,
    tags: project.tags || [],
    order: project.order ?? 1000,
  };
}

function sortProjects(projects: ProjectManifest[]): ProjectManifest[] {
  return [...projects].sort((left, right) => {
    if (Boolean(left.featured) !== Boolean(right.featured)) return left.featured ? -1 : 1;
    const byOrder = (left.order ?? 1000) - (right.order ?? 1000);
    if (byOrder !== 0) return byOrder;
    return left.title.localeCompare(right.title);
  });
}

export async function loadProjects(origin: string): Promise<LoadedProjects> {
  const fallback = sortProjects(createFallbackProjects(origin));
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${origin}/api/projects`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`Project registry returned HTTP ${response.status}`);

    const registry = (await response.json()) as ProjectRegistryResponse;
    if (registry.schemaVersion !== 1 || !Array.isArray(registry.projects)) {
      throw new Error('Project registry has an unsupported shape');
    }

    const projects = sortProjects(
      registry.projects
        .filter(isVisibleProject)
        .map((project) => normalizeProject(project, origin)),
    );

    // An empty, valid registry is authoritative. The fallback is only for
    // transport/protocol failures, not for an intentionally empty bucket.
    return { projects, source: 'remote' };
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return {
      projects: fallback,
      source: 'fallback',
      warning: `Using the built-in project catalog because the live registry is unavailable (${detail}).`,
    };
  } finally {
    window.clearTimeout(timeout);
  }
}
